import React, { useMemo } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { Sparkles, MapPin } from 'lucide-react';

// ----------------------------------------------------------------------
// RegionMap
// ----------------------------------------------------------------------
// Country-focused companion to LeadMapSection. Same visual language —
// starfield background, brand-red city markers with pulsing glow, gold
// arcs, dark navy palette — but rendered via a 2D `geoMercator`
// projection zoomed on a single country (Canada, UK, …).
//
// Kept intentionally lightweight (no d3-geo rotation, no mouse tilt) so
// it renders instantly under a Suspense fallback and stays under a few
// KB of runtime cost.
// ----------------------------------------------------------------------

const GEO_WORLD = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// Deterministic starfield — 90 points, generated once so re-renders are cheap.
const STARS = Array.from({ length: 90 }, (_, i) => {
  const seed = (i + 1) * 9301;
  const rnd = ((seed * 49297) % 233280) / 233280;
  const rnd2 = ((seed * 15731) % 78901) / 78901;
  const rnd3 = ((seed * 3571)  % 5443)  / 5443;
  return {
    cx: rnd * 100, cy: rnd2 * 100,
    r: 0.4 + rnd3 * 1.4,
    o: 0.35 + rnd3 * 0.65,
    twinkle: rnd3 > 0.65,
  };
});

// City marker with the same pulsing red-gold glow as LeadMapSection.
const CityMarker = ({ city, index }) => (
  <Marker coordinates={city.coords}>
    <g>
      <circle r={9} fill={city.color || '#ff5b5b'} opacity={0.18}>
        <animate attributeName='r' values='9;13;9' dur={`${3.6 + index * 0.15}s`} repeatCount='indefinite' />
        <animate attributeName='opacity' values='0.18;0.08;0.18' dur={`${3.6 + index * 0.15}s`} repeatCount='indefinite' />
      </circle>
      <circle r={3.4} fill={city.color || '#ff5b5b'} stroke='#fff' strokeWidth={0.8} />
      <text
        textAnchor={city.labelAnchor || 'middle'}
        dy={city.labelDy || -10}
        dx={city.labelDx || 0}
        style={{
          fontFamily: 'Inter, ui-sans-serif',
          fontSize: 9,
          fontWeight: 700,
          fill: '#fff',
          paintOrder: 'stroke',
          stroke: 'rgba(11,18,32,0.85)',
          strokeWidth: 3,
        }}
      >
        {city.name}
      </text>
    </g>
  </Marker>
);

export const RegionMap = ({
  countryName,      // exact match from world-atlas 110m (e.g., 'Canada', 'United Kingdom')
  cities,           // [{ name, coords: [lng, lat], color, labelDy, labelDx, labelAnchor }]
  eyebrow,          // small red-gold uppercase label above H2
  heading,          // H2 headline shown above the map
  subheading,       // small caption below the H2
  projectionConfig, // { center: [lng, lat], scale: N }
  aspect = '16/10',
  subdivisionsGeo,  // optional URL to a provinces/regions TopoJSON or GeoJSON
  subdivisionNameKey = 'name', // properties key that holds the region name
  onSubdivisionClick, // (regionName) => void — falls back to auto-scroll
}) => {
  const highlight = (name) => name === countryName;
  const cityCount = cities?.length || 0;

  const handleSubdivisionClick = (name) => {
    if (!name) return;
    if (onSubdivisionClick) {
      onSubdivisionClick(name);
      return;
    }
    // Fallback: scroll to a chip on the current page that carries id="region-<name>".
    // Slug in a kebab-safe form so both "Ontario" and "Newfoundland and Labrador"
    // resolve to a valid id.
    const id = 'region-' + name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const wrapperStyle = useMemo(() => ({
    aspectRatio: aspect,
    background: 'radial-gradient(circle at 50% 45%, rgba(23, 42, 89, 0.55) 0%, rgba(3, 8, 20, 0.95) 65%)',
  }), [aspect]);

  // Derive the inner SVG viewBox height from the wrapper aspect ratio so the
  // ComposableMap's projection uses the SAME height as the visible container.
  // Without this, a shorter wrapper aspect (e.g. 16/7 for Canada) would just
  // letterbox the SVG instead of actually cropping vertical whitespace. Width
  // stays fixed at 1000 so downstream city label positions still line up.
  const mapDims = useMemo(() => {
    const [w, h] = String(aspect).split('/').map(Number);
    const width = 1000;
    const height = w && h ? Math.round((width * h) / w) : 620;
    return { width, height };
  }, [aspect]);

  return (
    <section className='relative overflow-hidden bg-slate-950 border-y border-slate-800' data-testid='region-map'>
      {/* Header */}
      <div className='relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-6 text-center'>
        <div className='inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#7de3d9] mb-2'>
          <Sparkles className='w-3.5 h-3.5' /> {eyebrow || 'We welcome trainers from all over the world'}
        </div>
        <h2 className='font-display text-3xl md:text-4xl font-bold tracking-tight text-white'>{heading}</h2>
        {subheading && <p className='text-slate-300 mt-3 max-w-2xl mx-auto'>{subheading}</p>}
      </div>

      {/* Map + starfield */}
      <div className='relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-4'>
        <div className='relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl' style={wrapperStyle}>
          {/* Starfield backdrop — pure SVG so it never blocks pointer events */}
          <svg className='absolute inset-0 w-full h-full pointer-events-none' aria-hidden='true'>
            {STARS.map((s, i) => (
              <circle
                key={i}
                cx={`${s.cx}%`} cy={`${s.cy}%`} r={s.r} fill='#fff' opacity={s.o}
              >
                {s.twinkle && <animate attributeName='opacity' values={`${s.o};0.15;${s.o}`} dur='4.2s' repeatCount='indefinite' />}
              </circle>
            ))}
          </svg>

          {/* Country map */}
          <ComposableMap
            projection='geoMercator'
            projectionConfig={projectionConfig}
            width={mapDims.width}
            height={mapDims.height}
            style={{ width: '100%', height: '100%' }}
          >
            <defs>
              <radialGradient id='region-highlight' cx='50%' cy='50%' r='75%'>
                <stop offset='0%' stopColor='#C8102E' stopOpacity='0.85' />
                <stop offset='60%' stopColor='#1B3A87' stopOpacity='0.65' />
                <stop offset='100%' stopColor='#0B1220' stopOpacity='0.85' />
              </radialGradient>
              <linearGradient id='region-land' x1='0' x2='0' y1='0' y2='1'>
                <stop offset='0%' stopColor='#122036' />
                <stop offset='100%' stopColor='#0A1220' />
              </linearGradient>
            </defs>
            <Geographies geography={GEO_WORLD}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const name = geo.properties.name;
                  const isTarget = highlight(name);
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={isTarget ? 'url(#region-highlight)' : 'url(#region-land)'}
                      stroke={isTarget ? '#7de3d9' : 'rgba(120,140,180,0.25)'}
                      strokeWidth={isTarget ? 0.8 : 0.35}
                      style={{
                        default: { outline: 'none' },
                        hover:   { outline: 'none', fill: isTarget ? '#ffffff' : undefined },
                        pressed: { outline: 'none' },
                      }}
                    />
                  );
                })
              }
            </Geographies>

            {/* Optional subdivisions overlay — Canada provinces or UK regions.
                Each shape is clickable and scrolls to the matching chip on
                the page (id="region-<name>"). Loaded from a public GeoJSON
                so we don't ship the topology in the bundle. */}
            {subdivisionsGeo && (
              <Geographies geography={subdivisionsGeo}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const rname = geo.properties?.[subdivisionNameKey] || geo.properties?.name || '';
                    let subTouchNav = false;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill='rgba(200,16,46,0.10)'
                        stroke='rgba(255,255,255,0.55)'
                        strokeWidth={0.55}
                        onClick={(e) => { e.stopPropagation(); if (!subTouchNav) handleSubdivisionClick(rname); subTouchNav = false; }}
                        onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); subTouchNav = true; handleSubdivisionClick(rname); }}
                        style={{
                          default: { outline: 'none', pointerEvents: 'all', touchAction: 'manipulation' },
                          hover:   { outline: 'none', pointerEvents: 'all', fill: '#ffffff', stroke: '#ffffff' },
                          pressed: { outline: 'none', pointerEvents: 'all' },
                        }}
                      >
                        <title>{rname}</title>
                      </Geography>
                    );
                  })
                }
              </Geographies>
            )}
            {(cities || []).map((c, i) => <CityMarker key={c.name} city={c} index={i} />)}
          </ComposableMap>
        </div>

        {/* City-count chip removed per request. */}
      </div>
    </section>
  );
};

export default RegionMap;
